"use client"
import {
  ImageProps,
  SocialItemProps,
  blurDataUrl,
} from '@/src/common-types';
import { Container } from '@/src/components/container';
import { CustomLink } from '@/src/components/custom-link';
import { cn } from '@/src/utils/shadcn';
import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';
import {
  FaLinkedinIn,
  FaRegFolderOpen,
  FaRegUser,
} from 'react-icons/fa6';
import { FaFacebookF, FaTwitter, FaInstagram } from 'react-icons/fa6';
import axiosConfig from '@/src/utils/axiosConfig.ts';
import Editor from '@/src/components/editor';
import { HeroSection } from '../hero/v3';

interface AuthorProps {
  image: Omit<ImageProps, 'width' | 'height'>;
  name: string;
  about: string;
  socialLinks: SocialItemProps[];
}

const authorData: AuthorProps = {
  image: {
    src: '/assets/images/brand/logo-small.png',
    alt: 'author',
  },
  name: 'SOC',
  about: 'Smart Oasis is a pioneering leader in Iraq telecom and IT sectors',
  socialLinks: [
    {
      icon: <FaFacebookF />,
      href: 'https://www.facebook.com/smartoasis.offcial',
    },
    {
      icon: <FaLinkedinIn />,
      href: 'https://www.linkedin.com/company/smartoasisoffical/',
    },
    {
      icon: <FaInstagram />,
      href: 'https://www.instagram.com/smartoasis.official',
    },
  ],
};

const authorDataAr: AuthorProps = {
  image: {
    src: '/assets/images/brand/logo-small.png',
    alt: 'author',
  },
  name: 'واحة الذكاء',
  about: 'واحة الذكاء هي قوة رائدة في قطاعي الاتصالات وتكنولوجيا المعلومات في العراق',
  socialLinks: [
    {
      icon: <FaFacebookF />,
      href: 'https://www.facebook.com/smartoasis.offcial',
    },
    {
      icon: <FaLinkedinIn />,
      href: 'https://www.linkedin.com/company/smartoasisoffical/',
    },
    {
      icon: <FaInstagram />,
      href: 'https://www.instagram.com/smartoasis.official',
    },
  ],
};

interface NewsItemProps {
  id: string;
  imageUrl: string;
  category: string;
  date: string;
  title: string;
  description: string;
  categoryAr?: string;
  titleAr?: string;
  descriptionAr?: string;
}

function NewsItemCard({
  id,
  imageUrl,
  category,
  date,
  title,
  description,
  categoryAr,
  titleAr,
  descriptionAr
}: NewsItemProps) {
  const [isArabic, setIsArabic] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const lang = window.location.pathname.split("/")[1];
      setIsArabic(lang === "ar");
    }
  }, []);

  const displayCategory = isArabic && categoryAr ? categoryAr : category;
  const displayTitle = isArabic && titleAr ? titleAr : title;
  const displayDescription = isArabic && descriptionAr ? descriptionAr : description;

  const formattedDate = new Date(date).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="flex gap-4">
      <div className="w-[120px] h-[120px] flex-shrink-0 relative overflow-hidden rounded-md">
        <CustomLink href={`/${isArabic ? 'ar' : 'en'}/news/${id}`}>
          <Image
            src={imageUrl}
            alt={displayTitle}
            fill
            className="object-cover transition-transform duration-300 hover:scale-105"
            placeholder="blur"
            blurDataURL={blurDataUrl}
          />
        </CustomLink>
      </div>
      <div className="flex-grow">
        <div className="text-xs text-accent-700 dark:text-accent-300 mb-1">
          <span className="text-primary font-sm">{displayCategory}</span>
          <br />
          <div className="flex items-center gap-1">
            <div className="w-[14px] h-[14px] dark:[filter:brightness(0)_saturate(100%)_invert(42%)_sepia(37%)_saturate(604%)_hue-rotate(184deg)_brightness(91%)_contrast(93%)]">
              <Image src="/assets/images/icons/date.png" width={14} height={14} alt="date" />
            </div> {formattedDate}</div>
        </div>
        <h4 className="font-secondary text-sm font-bold leading-tight text-accent-900 dark:text-white mb-1">
          <CustomLink href={`/${isArabic ? 'ar' : 'en'}/news/${id}`} className="transition-colors duration-400 hover:text-primary ease-in-out">
            {displayTitle}
          </CustomLink>
        </h4>
        <p className="text-xs text-body line-clamp-2">{displayDescription}</p>
      </div>
    </div >
  );
}

interface NewsWidgetProps {
  newsItems: NewsItemProps[];
  title: string;
  titleAr: string;
}

function NewsWidget({ newsItems, title, titleAr }: NewsWidgetProps) {
  const [isArabic, setIsArabic] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const lang = window.location.pathname.split("/")[1];
      setIsArabic(lang === "ar");
    }
  }, []);

  return (
    <div className="space-y-5">
      <h3 className="font-secondary text-md leading-[1.25] text-accent-400 dark:text-white md:text-md mb-16">
        {isArabic ? titleAr : title}
      </h3>
      <nav aria-label={isArabic ? titleAr : title}>
        <ul className="grid gap-6">
          {newsItems.map((item) => (
            <li key={item.id}>
              <NewsItemCard {...item} />
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function CoverImageSlider({ images }: { images: { ImageUrl: string; Title: string }[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [images.length]);

  const handleThumbnailClick = (index: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCurrentIndex(index);
  };

  return (
    <section className="section-padding-primary relative flex min-h-[450px] items-center">
      <img
        alt={images[currentIndex].Title}
        src={images[currentIndex].ImageUrl}
        className="pointer-events-none object-cover"
        style={{ position: 'absolute', height: '100%', width: '100%', inset: 0, color: 'transparent' }}
      />
<span className="absolute bottom-0 left-0 w-full h-[100px] bg-gradient-to-t from-white to-transparent dark:from-accent-900 dark:to-accent-900/0"></span>
      <div className="thumbnails flex gap-2 mt-4 justify-center absolute bottom-4 w-full">
        {images.map((image, index) => (
          <div
            key={index}
            className={`thumbnail w-[60px] overflow-hidden rounded-md cursor-pointer border-2 ${
              currentIndex === index ? 'border-primary' : 'border-transparent'
            }`}
            onClick={() => handleThumbnailClick(index)}
          >
            <Image
              src={image.ImageUrl}
              alt={image.Title}
              width={60}
              height={60}
              className="object-cover"
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export function NewsListSection() {
  const [news, setNews] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isArabic, setIsArabic] = useState(false);
  const [idParam, setIdParam] = useState('');
  const [lang, setLang] = useState('en');
  const [latestNews, setLatestNews] = useState<NewsItemProps[]>([]);
  const [relatedNews, setRelatedNews] = useState<NewsItemProps[]>([]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const pathParts = window.location.pathname.split("/");
      const lang = pathParts[1];
      const id = pathParts[3];
      setIsArabic(lang === "ar");
      setIdParam(id);
    }
  }, []);

  const extractPlainText = (editorJsContent: string) => {
    try {
      const content = JSON.parse(editorJsContent);
      if (content?.blocks) {
        return content.blocks
          .map((block: any) => {
            if (block.type === 'paragraph' && block.data?.text) {
              return block.data.text.replace(/<[^>]*>/g, '');
            }
            if (block.type === 'list' && block.data?.items) {
              return block.data.items.map((item: any) => item.content.replace(/<[^>]*>/g, '')).join(' ');
            }
            return '';
          })
          .filter(Boolean)
          .join(' ');
      }
    } catch (e) {
      console.error("Error parsing EditorJS content:", e);
    }
    return '';
  };


  // Determine the language after the component mounts
  useEffect(() => {
    if (typeof window !== "undefined") {
      const lang = window.location.pathname.split("/")[1];
      setLang(lang);
      setIsArabic(lang === "ar");
    }
  }, []);

  useEffect(() => {
    const fetchNewsData = async () => {
      setLoading(true);
      if (idParam) {
        try {
          const langParam = isArabic ? 'ar' : 'en';
          // Fetch main news item
          const newsResponse = await axiosConfig(`/api/NewsItem/${idParam}?Language=${langParam}`);
          const newsData = newsResponse.data.Data;
          setNews(newsData);

          // Fetch related news
          if (newsData?.CategoryId) {
            const relatedResponse = await axiosConfig(`/api/NewsItem?Language=${langParam}`);
            const relatedData = relatedResponse.data.Data;

            const formattedRelatedNews = relatedData
              .filter((item: any) => item.Id !== idParam)
              .map((item: any) => ({
                id: item.Id,
                imageUrl: item.Images?.[0]?.ImageUrl || '/assets/images/placeholder.jpg',
                category: item.CategoryName || '',
                date: item.CreatedAt,
                title: item.Title || '',
                description: extractPlainText(item.Content) || '',
                categoryAr: item.CategoryName,
                titleAr: item.Title,
                descriptionAr: extractPlainText(item.Content),
              }))
              .slice(0, 5);

            setRelatedNews(formattedRelatedNews);
          }

          // Fetch latest news
          const latestResponse = await axiosConfig(`/api/NewsItem?pageSize=5&sortBy=createdAt&sortOrder=desc&Language=${langParam}`);
          const latestData = latestResponse.data.Data;

          const formattedLatestNews = latestData.map((item: any) => ({
            id: item.Id,
            imageUrl: item.Images?.[0]?.ImageUrl || '/assets/images/placeholder.jpg',
            category: item.CategoryName || '',
            date: item.CreatedAt,
            title: item.Title || '',
            description: extractPlainText(item.Content) || '',
            categoryAr: item.CategoryName,
            titleAr: item.Title,
            descriptionAr: extractPlainText(item.Content),
          }));

          setLatestNews(formattedLatestNews);
        } catch (error) {
          console.error('Error fetching news data:', error);
          setRelatedNews([]);
          setLatestNews([]);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchNewsData();
  }, [idParam, isArabic]);

  return (
    <>
      <HeroSection
        title={isArabic ? "الأخبار" : "News"}
        breadcrumbItems={[
          {
            label: isArabic ? "الرئيسية" : "Home",
            href: '/',
          },
          {
            label: isArabic ? "الأخبار" : "News",
          },
        ]}
        image={news?.Images?.[0]?.ImageUrl}
      />
      <section className="section-padding-primary">
        <Container>
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="rounded-full border">
                <Image
                  src="/assets/images/logo/logo.webp"
                  width={150}
                  height={100}
                  alt="logo"
                  className="rounded-full"
                />
              </div>
            </div>
          ) : (
            <div className="grid gap-30px lg:grid-cols-[1fr_410px]">
              <div>
                {news && (
                  <>
                    <CoverImageSlider images={news.Images} />
                    <div className="grid gap-10 lg:gap-20">
                      <div className="group">

                        <ul
                          aria-label="news meta list"
                          className="mb-4 mt-6 flex flex-wrap items-center gap-x-[1.125rem] gap-y-2 lg:mb-5 lg:mt-30px"
                        >
                          <li className="flex items-center gap-2.5 text-primary font-bold">
                            <CustomLink href="#" className="transition-colors duration-400 hover:text-primary ease-in-out">
                              {news.CategoryName}
                            </CustomLink>
                          </li>
                        </ul>
                        <h3 className="font-secondary text-lg font-bold leading-[1.25] text-accent-900 dark:text-white md:text-xl">
                          <span className={cn("transition-colors duration-400 hover:text-primary ease-in-out", 'text-accent-900 dark:text-white')}>
                            {isArabic && news.TitleAr ? news.TitleAr : news.Title || 'Untitled'}
                          </span>
                        </h3>
                        {news.Tags?.length > 0 && (
                          <div className="my-3">
                            <ul className="flex flex-wrap gap-2">
                              {news.Tags.map((tag: { Id: string; Tag: string }) => (
                                <li
                                  key={tag.Id}
                                  className="inline-flex items-center font-sm rounded-20 border border-gray-300 dark:border-gray-600 px-2.5 py-0.5 text-xs transition-colors duration-300 dark:bg-accent-900"
                                >
                                  {tag.Tag}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="my-4 h-px bg-body/30 lg:my-5"></div>
                        <div>
                          <Editor id={news.Id} data={isArabic && news.ContentAr ? news.ContentAr : news.Content} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 text-body">
                        <span>
                          {new Date(news.CreatedAt).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div>
                {relatedNews.length > 0 && (
                  <div className="mt-10">
                    <NewsWidget
                      newsItems={relatedNews}
                      title="Last News"
                      titleAr="أخر الاخبار"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Container>
      </section>
    </>
  );
}